import React from 'react';
import {
  Modal,
  Stack,
  Text,
  TextInput,
  Button,
  Group,
  Table,
  ScrollArea,
  Loader,
  Flex,
  Pagination,
  Checkbox
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { courseService } from 'services/lcms/course.service';

interface CopyCourseModalProps {
  opened: boolean;
  onClose: () => void;
  onCourseSelected: (_courseId: string) => void;
}

type CourseOption = {
  msLCMSCourseID: string;
  courseName: string;
  courseCode?: string;
  courseCategory?: string;
  competencyCategory?: string;
};

const CopyCourseModal: React.FC<CopyCourseModalProps> = ({ opened, onClose, onCourseSelected }) => {
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selectedCourseId, setSelectedCourseId] = React.useState<string | null>(null);

  // Reset when modal opens/closes
  React.useEffect(() => {
    if (!opened) {
      setSearchKeyword('');
      setPage(1);
      setSelectedCourseId(null);
    }
  }, [opened]);

  const { data, isFetching } = useQuery({
    queryKey: ['copy-course-modal', searchKeyword, page],
    queryFn: () =>
      courseService.list({
        search: searchKeyword,
        pageNumber: page,
        pageSize: 10
      }),
    placeholderData: (previousData) => previousData,
    enabled: opened
  });

  const courses: CourseOption[] = (data?.data || []).map((item: any) => ({
    msLCMSCourseID: item.msLCMSCourseID || item.courseId || item.id,
    courseName: item.courseName || item.name || '-',
    courseCode: item.courseCode || item.code,
    courseCategory: item.courseCategory || item.category,
    competencyCategory: item.competencyCategory
  }));

  const totalPages = data?.totalPages || 1;

  const handleConfirm = () => {
    if (selectedCourseId) {
      onCourseSelected(selectedCourseId);
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size='lg'>
          Copy From Other Course
        </Text>
      }
      size='xl'
      centered
    >
      <Stack gap='lg'>
        <Text size='sm' c='dimmed'>
          Select a course to copy its details to the current form. This will populate all fields
          with the selected course's information.
        </Text>

        <TextInput
          placeholder='Search courses by name, code, or category'
          value={searchKeyword}
          onChange={(event) => {
            setSearchKeyword(event.currentTarget.value);
            setPage(1);
          }}
          leftSection={<IconSearch size={16} stroke={1.6} />}
          radius='md'
        />

        {isFetching ? (
          <Flex justify='center' align='center' h={200}>
            <Loader size='md' />
          </Flex>
        ) : courses.length === 0 ? (
          <Stack align='center' gap='md' py='xl'>
            <Text size='sm' c='dimmed' ta='center'>
              No courses found.
            </Text>
            <Button
              variant='light'
              onClick={() => {
                window.open('/app/lcms/courses/add', '_blank');
              }}
            >
              Add New Course
            </Button>
          </Stack>
        ) : (
          <ScrollArea h={400} offsetScrollbars>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 70 }}>Select</Table.Th>
                  <Table.Th>Course Name</Table.Th>
                  <Table.Th>Course Code</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Competency</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {courses.map((course) => (
                  <Table.Tr key={course.msLCMSCourseID}>
                    <Table.Td>
                      <Checkbox
                        checked={selectedCourseId === course.msLCMSCourseID}
                        onChange={() => {
                          setSelectedCourseId(
                            selectedCourseId === course.msLCMSCourseID
                              ? null
                              : course.msLCMSCourseID
                          );
                        }}
                        aria-label={`Select ${course.courseName}`}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text fw={600} size='sm'>
                          {course.courseName}
                        </Text>
                        <Text size='xs' c='dimmed'>
                          {course.msLCMSCourseID}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>{course.courseCode || '-'}</Table.Td>
                    <Table.Td>{course.courseCategory || '-'}</Table.Td>
                    <Table.Td>{course.competencyCategory || '-'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        <Group justify='center'>
          <Pagination total={Math.max(totalPages, 1)} value={page} onChange={setPage} />
        </Group>

        <Group justify='flex-end'>
          <Button variant='default' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedCourseId}>
            Copy Course Data
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CopyCourseModal;
